/**
 * API Client Configuration
 * 
 * This module provides a configured Axios instance and API client wrapper
 * for making requests to the People Rock backend API.
 * 
 * Features:
 * - Automatic JWT token injection from the current tab with a localStorage migration fallback
 * - Automatic language header injection from i18n
 * - Request/response interceptors for error handling
 * - TypeScript type safety with generated API client
 */

import axios, { AxiosError, AxiosInstance } from 'axios';
import { CONSENT_ROUTE, isConsentGateExempt } from '../config/consentGate';
import {
  Configuration,
  AuthenticationApi,
  UsersApi,
  LocalesApi,
  DevelopmentPlansApi,
  AssignmentProgressApi,
  DevelopmentAreasApi,
  DevelopmentAreasAdminApi,
  GDPRPrivacyApi,
  type ActiveRoleRequest,
} from './generated';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const AUTH_TOKEN_KEY = 'auth_token';

/**
 * Create configured Axios instance with interceptors.
 *
 * `baseURL` is intentionally omittable: the generated OpenAPI client's request paths
 * already include the full `/api/...` prefix (they're generated verbatim from the
 * backend's `@RequestMapping` paths), so an instance used by that client must NOT also
 * carry `/api` as its own baseURL — doing so doubles the prefix to `/api/api/...` and
 * every generated-client call 403s. Hand-written `axiosInstance.get('/content/...')`
 * calls elsewhere in the app expect the opposite: relative paths without `/api`, resolved
 * via `baseURL`. Both share the same interceptors, so the two instances are built by the
 * same factory with different `baseURL` values instead of duplicating interceptor logic.
 */
const createAxiosInstance = (baseURL?: string): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor: Add JWT token and language header
  instance.interceptors.request.use(
    (config) => {
      // Add JWT token if available
      const token = sessionStorage.getItem(AUTH_TOKEN_KEY) || localStorage.getItem(AUTH_TOKEN_KEY);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add language header from i18n
      const language = localStorage.getItem('i18nextLng') || 'en';
      config.headers['Accept-Language'] = language.split('-')[0]; // pt-PT -> pt

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor: Handle errors globally
  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      // Handle 401 Unauthorized - token expired or invalid
      if (error.response?.status === 401) {
        sessionStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(AUTH_TOKEN_KEY);
        // Optionally redirect to login page
        // window.location.href = '/login';
      }

      // Handle 403 Forbidden - insufficient permissions
      if (error.response?.status === 403) {
        const errorCode = (error.response.data as { error?: string } | undefined)?.error;

        // Server-side forced password rotation (bootstrap/admin-created accounts) blocks every
        // endpoint until the password is changed. Redirect there instead of surfacing a generic
        // "access denied" error on whatever tab triggered the request.
        if (errorCode === 'PASSWORD_CHANGE_REQUIRED') {
          if (window.location.pathname !== '/change-password') {
            window.location.assign('/change-password');
          }
        } else if (errorCode === 'CONSENT_REQUIRED') {
          // Server-side consent gate: the Terms / Privacy Policy (version in force) have not been
          // accepted. Send the user to the consent page, unless they are already on a page that is
          // reachable while the gate is up (avoids a redirect loop).
          if (!isConsentGateExempt(window.location.pathname)) {
            sessionStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
            window.location.assign(CONSENT_ROUTE);
          }
        } else {
          console.error('Access denied: insufficient permissions');
        }
      }

      // Handle 500 Internal Server Error
      if (error.response?.status === 500) {
        console.error('Server error:', error.response.data);
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

// Create Axios instance (exported for reuse by non-generated API modules).
// Relative paths written elsewhere (e.g. axiosInstance.get('/content/courses')) are
// resolved against this baseURL.
export const axiosInstance = createAxiosInstance(API_BASE_URL);

// Axios instance for the generated OpenAPI client only. Its request paths already
// include the full `/api/...` prefix, so this instance must not also carry `/api` as a
// baseURL path segment — but when API_BASE_URL is an absolute URL (e.g. local dev/CI:
// http://localhost:8080/api), the origin (protocol+host+port) still needs to be kept,
// otherwise requests resolve against the frontend's own origin instead of the backend's.
// When API_BASE_URL is relative (e.g. prod behind a same-origin reverse proxy: '/api'),
// there's no origin to extract, so no baseURL is set at all.
const GENERATED_API_ORIGIN = (() => {
  try {
    return new URL(API_BASE_URL).origin;
  } catch {
    return undefined;
  }
})();
const generatedApiAxiosInstance = createAxiosInstance(GENERATED_API_ORIGIN);

// Create OpenAPI Configuration
const apiConfig = new Configuration({
  basePath: '',
});

/**
 * API Client - Pre-configured API instances
 * 
 * Use these instances in your React components with React Query hooks
 * 
 * @example
 * ```tsx
 * import { apiClient } from '@/api/client';
 * 
 * // In your component:
 * const { data } = useQuery({
 *   queryKey: ['user', 'me'],
 *   queryFn: async () => {
 *     const response = await apiClient.users.usersMe();
 *     return response.data;
 *   }
 * });
 * ```
 */
export const apiClient = {
  /**
   * Authentication API - Register, Login
   */
  auth: new AuthenticationApi(apiConfig, undefined, generatedApiAxiosInstance),

  /**
   * Users API - Get current user, list users, get user by ID
   */
  users: new UsersApi(apiConfig, undefined, generatedApiAxiosInstance),

  /**
   * Locales API - Get locales, messages, batch messages
   */
  locales: new LocalesApi(apiConfig, undefined, generatedApiAxiosInstance),

  /**
   * Development Plans API (Phase 7) - CRUD operations for development plans
   */
  developmentPlans: new DevelopmentPlansApi(apiConfig, undefined, generatedApiAxiosInstance),

  /**
   * Assignment Progress API (Phase 7) - Track assignment completion progress
   */
  assignmentProgress: new AssignmentProgressApi(apiConfig, undefined, generatedApiAxiosInstance),

  /**
   * Development Areas API - active areas/dimensions catalogue (any authenticated user)
   */
  developmentAreas: new DevelopmentAreasApi(apiConfig, undefined, generatedApiAxiosInstance),

  /**
   * Development Areas admin API - CRUD for areas and dimensions (ADMIN)
   */
  developmentAreasAdmin: new DevelopmentAreasAdminApi(apiConfig, undefined, generatedApiAxiosInstance),

  /**
   * GDPR & Privacy API - consents (incl. the required Terms/Privacy consent gate), export, deletion
   */
  gdpr: new GDPRPrivacyApi(apiConfig, undefined, generatedApiAxiosInstance),
};

/**
 * Auth Utilities
 */
export const auth = {
  /**
   * Save the active JWT token to this browser tab.
   */
  setToken: (token: string) => {
    sessionStorage.setItem(AUTH_TOKEN_KEY, token);
  },

  /**
   * Get the tab token, falling back to the legacy localStorage bridge during migration.
   */
  getToken: (): string | null => {
    return sessionStorage.getItem(AUTH_TOKEN_KEY) || localStorage.getItem(AUTH_TOKEN_KEY);
  },

  /**
   * Remove both the tab token and the legacy shared bridge on logout.
   */
  clearToken: () => {
    sessionStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_TOKEN_KEY);
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated: (): boolean => {
    return !!(sessionStorage.getItem(AUTH_TOKEN_KEY) || localStorage.getItem(AUTH_TOKEN_KEY));
  },
};

/**
 * Session endpoints delegated to the generated OpenAPI client.
 */
export const authSessionApi = {
  selectActiveRole: (request: ActiveRoleRequest) =>
    apiClient.auth.selectActiveRole({ activeRoleRequest: request }),
  getSession: () => apiClient.auth.getSession1(),
};

/**
 * Export types from generated API
 */
export type {
  ActiveRoleRequest,
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  RequiredConsentDto,
  UserResponse,
  LocaleInfo,
  MessageResponse,
  BatchMessageRequest,
  BatchMessageResponse,
} from './generated';

export default apiClient;
